import { useCallback, useState } from "react";
import { useApp } from "../../../../context/AppContext";
import { notifyError, notifyWarning } from "../../../../utils/notify";
import {
  DEFAULT_TIMEZONE,
  fakeLocalDateToSalonISO,
} from "../../../../utils/timeUtils";
import { CalendarEventType, TimeBlockEvent } from "../../../../types/calendar";
import {
  CalendarEvent as CalendarBookedEvent,
  EditBookedServiceRequest,
  EditTransactionLineItemsRequest,
  EditTransactionLineItemsResponse,
} from "../../../../types/transactions";
import {
  CalendarDropInfo,
  CalendarResizeInfo,
} from "../components/Views/DnDCalendar";
import {
  SNAP_INCREMENT_MINS,
  rescheduleFiresClientSms,
  resolveResize,
  snapToIncrement,
} from "../calendarDragHelpers";
import { replaceEventInList } from "../calendarEventUtils";
import { useTypedCalendarEvent } from "../calendarViewTypes";
import type { SalonService } from "../../../../types/salon";
import { staffServiceFor } from "../booking/bookingTypes";
import {
  BlockedStaff,
  blockedDropToast,
  blockedStaffFor,
} from "../booking/skillBlock";

interface DragStaff {
  id: string;
  first_name?: string;
  last_name?: string;
}

const staffName = (staff: DragStaff[], id: string) => {
  const s = staff.find((x) => x.id === id);
  return s ? `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim() || id : id;
};

// A drag/resize the user must confirm before it writes. The handler stages the
// resolved values plus a `commit` closure that performs the API call; the
// confirmation modal runs `commit` on confirm or `revert` on cancel.
export interface PendingDragConfirm {
  title: string;
  action: "move" | "resize";
  // Whether committing this change texts the client (start_dt shift on a booked
  // appointment). Drives the SMS warning in the modal.
  firesSms: boolean;
  newStart: Date;
  newEnd: Date;
  // Persist the change (write already reflected optimistically on the grid).
  commit: () => Promise<void>;
  // Undo the optimistic move — used on Cancel and on API failure.
  revert: () => void;
}

interface UseDragRescheduleArgs {
  timezone: string | undefined;
  propagateBookedServiceEdit: (
    transactionId: string,
    responseLineItems: EditTransactionLineItemsResponse["line_items"],
  ) => void;
  /** Staff ↔ service source for a drop on another staff column. */
  salonServices: SalonService[];
  /** The day view's staff columns (names; the blocked set while dragging). */
  staff: DragStaff[];
}

/**
 * Drag-move / resize with a confirmation gate. RBC calls onEventDrop /
 * onEventResize the instant the mouse is released, so a fat-finger drag would
 * otherwise write (and text the client) with no chance to back out. The move
 * is applied optimistically on drop; confirm persists it, cancel reverts it.
 */
export function useDragReschedule({
  timezone: salonTimezone,
  propagateBookedServiceEdit,
  salonServices,
  staff,
}: UseDragRescheduleArgs) {
  const { updateTimeBlock, editTransactionLineItems } = useApp();
  const { setEvents } = useTypedCalendarEvent();

  const [pendingDragConfirm, setPendingDragConfirm] =
    useState<PendingDragConfirm | null>(null);
  const [isConfirmingDrag, setIsConfirmingDrag] = useState(false);

  // While an appointment is dragged: the staff columns that don't offer its
  // service (hatched, like the booking drawer's). Cleared on release.
  const [dragBlockedStaff, setDragBlockedStaff] = useState<BlockedStaff | null>(
    null,
  );
  const handleDragStart = useCallback(
    ({ event }: { event: CalendarEventType }) => {
      if (event.isTimeBlock) return;
      setDragBlockedStaff(
        blockedStaffFor(
          { salonServiceId: (event as CalendarBookedEvent).salonServiceId },
          salonServices,
          staff.map((s) => s.id),
        ),
      );
      window.addEventListener("mouseup", () => setDragBlockedStaff(null), {
        once: true,
      });
    },
    [salonServices, staff],
  );

  // Build the closure that persists a CONFIRMED reschedule. The optimistic move
  // has ALREADY been applied on drop (so the event stays where the operator let
  // go while the modal is open), so this only performs the API write, the
  // response propagate, and — on failure — calls `revert` to put the event back.
  // Shared by drag-move and resize so the write logic lives in one place.
  const buildRescheduleCommit = useCallback(
    (
      event: CalendarEventType,
      newStart: Date,
      newEnd: Date,
      bookedFields: Partial<EditBookedServiceRequest>,
      failureTitle: string,
      timezone: string,
      revert: () => void,
    ) =>
      async () => {
        try {
          if (event.isTimeBlock) {
            // Recurring blocks apply to the dragged occurrence only
            // (applies_to: "single"); a follow-up can introduce a prompt for
            // "hereafter" / "all". No response write-through: the optimistic
            // move applied on drop is the only local update on this path.
            const timeBlock = event as TimeBlockEvent;
            await updateTimeBlock({
              id: timeBlock.id,
              start_dt: fakeLocalDateToSalonISO(newStart, timezone),
              end_dt: fakeLocalDateToSalonISO(newEnd, timezone),
              applies_to: "single",
            });
            return;
          }

          const booked = event as CalendarBookedEvent;
          if (!booked.lineItemId || !booked.transactionId) {
            throw new Error(
              "Cannot reschedule: missing line_item_id or transactionId",
            );
          }
          const payload: EditTransactionLineItemsRequest = {
            transaction_id: booked.transactionId,
            line_items: {
              booked_services: [
                {
                  id: booked.id,
                  line_item_id: booked.lineItemId,
                  ...bookedFields,
                },
              ],
            },
          };
          const response = await editTransactionLineItems(payload);
          if (response?.line_items) {
            propagateBookedServiceEdit(
              booked.transactionId,
              response.line_items,
            );
          }
        } catch (error) {
          // Write rejected → undo the optimistic move.
          revert();
          const message =
            error instanceof Error ? error.message : "Failed to reschedule";
          notifyError(message, { title: failureTitle });
        }
      },
    [updateTimeBlock, editTransactionLineItems, propagateBookedServiceEdit],
  );

  // Apply the optimistic move for a drag/resize immediately (on drop) and hand
  // back a `revert` that restores just this event to its pre-move position — a
  // functional update, so a concurrent Pusher/refetch on other events is not
  // clobbered. Called before the confirmation modal opens so the event stays
  // where the operator let go.
  const placeEventOptimistically = useCallback(
    (
      event: CalendarEventType,
      newStart: Date,
      newEnd: Date,
      newStaff?: { id: string; name: string },
    ) => {
      const booked = event as CalendarBookedEvent;
      const original = {
        start: event.start,
        end: event.end,
        resourceId: event.resourceId,
        ...(newStaff
          ? { employeeId: booked.employeeId, employeeName: booked.employeeName }
          : {}),
      };
      setEvents((prev) =>
        replaceEventInList(prev, event.id, {
          start: newStart,
          end: newEnd,
          ...(newStaff
            ? {
                resourceId: newStaff.id,
                employeeId: newStaff.id,
                employeeName: newStaff.name,
              }
            : {}),
        }),
      );
      return () =>
        setEvents((prev) => replaceEventInList(prev, event.id, original));
    },
    [setEvents],
  );

  const handleEventDrop = useCallback(
    (info: CalendarDropInfo) => {
      setDragBlockedStaff(null);
      const { event, start } = info;
      const booked = event as CalendarBookedEvent;
      // Day view: a drop on another staff column reassigns the appointment
      // to that staff member (their staff_service), if they offer the service.
      const toStaff =
        info.resourceId &&
        event.resourceId &&
        String(info.resourceId) !== String(event.resourceId)
          ? String(info.resourceId)
          : null;
      let staffServiceId: string | undefined;
      if (toStaff) {
        if (event.isTimeBlock) {
          notifyWarning("Time blocks stay with their staff member.", {
            title: "Can’t move a time block to another staff member",
          });
          return;
        }
        const svc = salonServices.find((s) => s.id === booked.salonServiceId);
        staffServiceId = staffServiceFor(svc, toStaff)?.id;
        if (!staffServiceId) {
          const t = blockedDropToast(
            svc?.name ?? event.title,
            staffName(staff, toStaff),
          );
          notifyError(t.note, { title: t.title });
          return;
        }
      }

      // Compute the snapped target FIRST, then check for a no-op against
      // the snapped values. A few-pixel drag that snapToIncrement collapses
      // back to the event's existing start should NOT open the modal.
      const snappedStart = snapToIncrement(start, SNAP_INCREMENT_MINS);
      // Preserve the appointment's existing duration on a pure move; RBC's
      // `end` reflects start+duration so the snap only needs to anchor on start.
      const durationMs = event.end.getTime() - event.start.getTime();
      const snappedEnd = new Date(snappedStart.getTime() + durationMs);
      const timeChanged = event.start.getTime() !== snappedStart.getTime();
      if (!timeChanged && !toStaff) return;

      const timezone = salonTimezone || DEFAULT_TIMEZONE;
      // Move it now so it stays where the operator dropped it while the modal
      // is open; `revert` puts it back on cancel or API failure.
      const revert = placeEventOptimistically(
        event,
        snappedStart,
        snappedEnd,
        toStaff ? { id: toStaff, name: staffName(staff, toStaff) } : undefined,
      );
      setPendingDragConfirm({
        title: event.title,
        action: "move",
        // Only a start_dt shift texts the client; a staff-only move doesn't.
        firesSms: rescheduleFiresClientSms({
          isTimeBlock: !!event.isTimeBlock,
          hasLineItem: !!booked.lineItemId,
          hasTransaction: !!booked.transactionId,
          startChanged: timeChanged,
        }),
        newStart: snappedStart,
        newEnd: snappedEnd,
        revert,
        commit: buildRescheduleCommit(
          event,
          snappedStart,
          snappedEnd,
          {
            ...(timeChanged
              ? { start_dt: fakeLocalDateToSalonISO(snappedStart, timezone) }
              : {}),
            ...(staffServiceId ? { staff_service_id: staffServiceId } : {}),
          },
          "Could not reschedule",
          timezone,
          revert,
        ),
      });
    },
    [
      buildRescheduleCommit,
      placeEventOptimistically,
      salonTimezone,
      salonServices,
      staff,
    ],
  );

  const handleEventResize = useCallback(
    (info: CalendarResizeInfo) => {
      const { event, start, end } = info;
      if (
        event.start.getTime() === start.getTime() &&
        event.end.getTime() === end.getTime()
      ) {
        return;
      }

      const { snappedStart, baseDurationMins, finalEnd, startChanged } =
        resolveResize(event, start, end);

      // No-op if the snapped result lands back on the original boundaries.
      if (
        event.start.getTime() === snappedStart.getTime() &&
        event.end.getTime() === finalEnd.getTime()
      ) {
        return;
      }

      const booked = event as CalendarBookedEvent;
      const timezone = salonTimezone || DEFAULT_TIMEZONE;
      // Apply the resize now so it holds while the modal is open; `revert`
      // restores it on cancel or API failure.
      const revert = placeEventOptimistically(event, snappedStart, finalEnd);
      setPendingDragConfirm({
        title: event.title,
        action: "resize",
        // Only a top-edge (start-changed) resize fires the reschedule SMS.
        firesSms: rescheduleFiresClientSms({
          isTimeBlock: !!event.isTimeBlock,
          hasLineItem: !!booked.lineItemId,
          hasTransaction: !!booked.transactionId,
          startChanged,
        }),
        newStart: snappedStart,
        newEnd: finalEnd,
        revert,
        // Top-edge resize (start changed): send start_dt + base duration so the
        // appointment shifts + grows/shrinks. Bottom-edge resize (start
        // unchanged): omit start_dt so the backend short-circuits the
        // reschedule SMS branch (new_earliest_start_time unchanged).
        commit: buildRescheduleCommit(
          event,
          snappedStart,
          finalEnd,
          {
            base_duration_mins: baseDurationMins,
            ...(startChanged
              ? { start_dt: fakeLocalDateToSalonISO(snappedStart, timezone) }
              : {}),
          },
          "Could not update appointment",
          timezone,
          revert,
        ),
      });
    },
    [buildRescheduleCommit, placeEventOptimistically, salonTimezone],
  );

  const handleConfirmDrag = useCallback(async () => {
    if (!pendingDragConfirm) return;
    setIsConfirmingDrag(true);
    try {
      await pendingDragConfirm.commit();
    } finally {
      setIsConfirmingDrag(false);
      setPendingDragConfirm(null);
    }
  }, [pendingDragConfirm]);

  // Cancel: undo the optimistic move (put the event back where it started) and
  // drop the staged commit. Ignored while a commit is in flight so Escape /
  // click-outside can't tear the modal down mid-write.
  const handleCancelDrag = useCallback(() => {
    if (isConfirmingDrag) return;
    pendingDragConfirm?.revert();
    setPendingDragConfirm(null);
  }, [isConfirmingDrag, pendingDragConfirm]);

  return {
    pendingDragConfirm,
    isConfirmingDrag,
    dragBlockedStaff,
    handleDragStart,
    handleEventDrop,
    handleEventResize,
    handleConfirmDrag,
    handleCancelDrag,
  };
}
